/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import {
  BrickLoader,
  Button,
  FlexContainer,
  InputSearch,
  theme,
} from "@nordcloud/gnui";
import { SearchV2SortField } from "~/generated/graphql";
import { ErrorWrap, LoaderWrap, Pagination, ReactTableV2 } from "~/components";
import { EstatePeriod } from "~/constants";
import { useCurrency, useEstateRecordsV2, useQueryState } from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { showError } from "~/services/toast";
import { SearchSortOrder, isNotEmpty } from "~/tools";
import { AccumulatedCostData } from "~/views/estate/components/Charts/AccumulatedCostData";
import { FiltersSidebar } from "~/views/estate/components/FiltersSidebar";
import { useEstateFilters } from "~/views/estate/components/hooks";
import { CloudProviderType, Provider } from "../../types";
import { EstateContainer } from "./EstateContainer";
import { EstateListColumns } from "./EstateList/EstateListColumns";
import { generateNewRowData } from "./EstateList/helpers";
import { EstateRecordsNotification } from "./EstateRecordsNotification";
import { SearchBarContainer } from "./SearchBar";

type Props = {
  providerId: string;
  providerType?: CloudProviderType;
  provider: Provider;
};

export function EstateRecords({ provider, providerType, providerId }: Props) {
  const { state, setQueryState } = useQueryState<{
    field: SearchV2SortField;
    order: SearchSortOrder;
  }>();
  const { page, limit, field, order, query } = state;
  const { currency } = useCurrency();
  const { isCurrentBillingPeriod } = useBillingPeriod();

  const [searchKey, setSearchKey] = useState(query ?? "");

  const { data, loading, error } = useEstateRecordsV2(
    {
      variables: {
        page,
        limit,
        filter: {
          account: [providerId],
        },
        query,
        sort: {
          order: order === "ASC" ? SearchSortOrder.Asc : SearchSortOrder.Dsc,
          field,
        },
      },
    },
    showError,
    "2000.01.01"
  );

  const rows = useMemo(
    () =>
      data?.resources?.map((res) => ({
        ...generateNewRowData(res, currency),
      })) ?? [],
    [data, currency]
  );

  const estateRecords = data?.resources ?? [];

  const hasData = isNotEmpty(estateRecords);
  const count = data?.count ?? 0;

  const {
    filters,
    isSidebarOpen,
    handleUpdate,
    clearFilters,
    toggleFilters,
    toggleIsSidebarOpen,
  } = useEstateFilters();

  const onSort = ({ id, desc }: { id: string; desc: boolean }) => {
    setQueryState({
      ...state,
      page: 0,
      limit: 20,
      order: desc ? SearchSortOrder.Dsc : SearchSortOrder.Asc,
      field: (id ?? SearchV2SortField.Cost) as SearchV2SortField,
    });
  };

  const defaultSort = {
    field: SearchV2SortField.Cost,
    order: SearchSortOrder.Dsc,
  };

  const columns = useMemo(() => EstateListColumns(), []);
  const period = isCurrentBillingPeriod
    ? EstatePeriod.DEFAULT_MONTH
    : EstatePeriod.PREVIOUS_MONTH;

  useEffect(() => {
    handleUpdate({ provider: [provider], account: [providerId] });
  }, [providerId, provider]);

  useEffect(() => {
    setQueryState(defaultSort);
  }, []);

  return (
    <>
      <EstateContainer>
        <ErrorWrap error={error}>
          <LoaderWrap loading={loading} Component={BrickLoader}>
            <FlexContainer
              direction="column"
              alignItems="flex-start"
              gap={theme.spacing.spacing04}
            >
              <When condition={hasData}>
                <EstateRecordsNotification
                  provider={provider}
                  providerType={providerType}
                />
              </When>
              <SearchBarContainer>
                <div css={{ width: "100%" }}>
                  <InputSearch
                    value={searchKey}
                    onChange={(event) => setSearchKey(event.target.value)}
                    onSearch={() =>
                      setQueryState({ ...state, query: searchKey, page: 0 })
                    }
                  />
                </div>
                <Button onClick={toggleIsSidebarOpen}>Filters</Button>
              </SearchBarContainer>
              <When condition={hasData}>
                <AccumulatedCostData
                  hidePeriod
                  period={period}
                  totalCost={Number(data?.cost?.currentMonthTotal)}
                  count={count}
                  monthlySavings={data?.savingsAmount?.total ?? "0"}
                  isLoading={loading}
                  forecast={data?.cost?.monthlyForecast ?? "0"}
                  averageCost={data?.cost?.currentMonthTotal}
                />
              </When>
              <div css={{ width: "100%" }}>
                <ReactTableV2
                  striped
                  sort
                  columns={columns}
                  data={rows}
                  headerBackground={theme.color.background.ui03}
                  onSort={onSort}
                />
              </div>
              <Pagination count={count} />
            </FlexContainer>
          </LoaderWrap>
        </ErrorWrap>
      </EstateContainer>
      <FiltersSidebar
        hiddenFilters={["account", "provider"]}
        isSidebarOpen={isSidebarOpen}
        filters={filters}
        toggleIsSidebarOpen={toggleIsSidebarOpen}
        updateFilters={handleUpdate}
        toggleFilters={toggleFilters}
        clearFilters={clearFilters}
      />
    </>
  );
}
